import { Slide, TableCell } from '@features/powerpoint';
import { contentSlideHeader, generateTable } from '@features/lci-report/utils';
import { formatters } from '@utils/formattersV2';
import { Lci3SalesMetrics } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { RslReport } from '@features/campaigns/subdomains/rsl';

export const TABLE_HEADER_OPTIONS = {
  color: unifiedPlatformColorPalette.inmarketGreen,
  bold: true,
};

type RslSubdimension = {
  salesMetrics: Lci3SalesMetrics | null;
  visitMetrics: {
    impressions: number;
    uniqueUsers: number;
  };
  value: string;
};

type RslDimensionsTableSlideProps = {
  slideMaster: string;
  subdimensions: RslSubdimension[];
  overallSalesMetrics: Lci3SalesMetrics;
  overallReport: Pick<RslReport, 'impressions' | 'uniqueUsers'>;
  slideHeader: string;
};

const rslDimensionsColumnLabels = [
  {
    text: 'Dimension',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Total Impressions',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Unique Users Reached',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Incremental Sales Lift',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Incremental Sales',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Transaction Size Lift',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Transaction Rate Lift',
    options: TABLE_HEADER_OPTIONS,
  },
  {
    text: 'Penetration Lift',
    options: TABLE_HEADER_OPTIONS,
  },
] as const satisfies TableCell[];

const formatMetrics = (
  salesMetrics: Lci3SalesMetrics | null,
  impressions: number,
  uniqueUsers: number | null,
  value: string
): TableCell[] => {
  const incrementalSalesLift = formatters.lift(
    salesMetrics?.incrementalSalesLift.total
  );

  const incrementalSales = formatters.currency(
    salesMetrics?.incrementalSales.total,
    { full: true }
  );

  const transactionSizeLift = formatters.percentage(
    salesMetrics?.transactionSizeLift.total,
    { decimals: 1 }
  );

  const transactionRateLift = formatters.percentage(
    salesMetrics?.transactionRateLift.total,
    { decimals: 1 }
  );

  const penetrationLift = formatters.percentage(
    salesMetrics?.penetrationLift.total,
    { decimals: 1 }
  );

  const uniqueUsersReached = formatters.users(uniqueUsers ?? 0, {
    full: true,
  });

  const totalImpressions = formatters.impressions(impressions, {
    full: true,
  });

  return [
    { text: value },
    { text: totalImpressions },
    { text: uniqueUsersReached },
    { text: incrementalSalesLift },
    { text: incrementalSales },
    { text: transactionSizeLift },
    { text: transactionRateLift },
    { text: penetrationLift },
  ];
};

const subdimensionRows = (
  subdimensions: RslSubdimension[],
  overallSalesMetrics: Lci3SalesMetrics,
  overallReport: RslDimensionsTableSlideProps['overallReport']
): TableCell[][] => {
  const overallRowData = formatMetrics(
    overallSalesMetrics,
    overallReport.impressions,
    overallReport.uniqueUsers,
    'Overall'
  );

  const subdimensionRowData = subdimensions.map(subdimension =>
    formatMetrics(
      subdimension.salesMetrics,
      subdimension.visitMetrics.impressions,
      subdimension.visitMetrics.uniqueUsers,
      subdimension.value
    )
  );

  return [overallRowData, ...subdimensionRowData];
};

export const rslDimensionsTableSlide = (
  props: RslDimensionsTableSlideProps
): Slide => {
  return {
    slideMaster: props.slideMaster,
    tables: [
      generateTable(
        rslDimensionsColumnLabels,
        subdimensionRows(
          props.subdimensions,
          props.overallSalesMetrics,
          props.overallReport
        )
      ),
    ],
    texts: [contentSlideHeader(props.slideHeader)],
  };
};
